import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import {
  clickAnimated,
  clickFirstRowIn,
  delay,
  tableSettledEmpty,
  typeInto,
} from '@/utils/tour/actions';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for Contract Management Views, told in creation
 * order: first HOW a view is built (name it, pick contracts, define columns,
 * set visibility), then a finished view with real extracted values, then
 * the column editor where prompts and filters live.
 *
 * The builder walk is write-free: the wizard keeps everything in component
 * state and only "Create View" (never clicked) persists anything — leaving
 * the builder discards the demo draft by construction. Both drawers are
 * opened and closed again, so nothing is left hanging.
 */

// Whether the demo actually opened a finished view — set synchronously by
// the open action, so the detail scenes' prerequisites can skip instantly
// on orgs with no views instead of burning their target timeouts one by
// one. The route check covers resumes that land directly on the detail.
let viewOpened = false;

async function openFirstView(ctx: TourActionContext): Promise<void> {
  viewOpened = false;
  // The list is a Gaia data card, not an n-data-table — its rows are grid divs,
  // so the default `tbody tr` selector would never match. Skeletons carry the
  // same class while loading and aren't clickable, hence the :not().
  viewOpened = await clickFirstRowIn(ctx, 'views.list-table', '.row:not(.skeleton)');
}

const viewOpen = (): boolean =>
  viewOpened || getRouter().currentRoute.value.name === 'contract-management-view-detail';

/** Back through history — the builder and detail pages stack on the list. */
async function goBackInHistory(): Promise<void> {
  getRouter().back();
  await delay(500);
}

/**
 * Advance the builder. The anchor only exists on the header's Continue
 * button (the final "Create View" is a different, unanchored element), so
 * this can never submit; a Continue disabled by step gating just skips.
 */
async function clickWizardContinue(ctx: TourActionContext): Promise<void> {
  const button = await waitForTourElement('views.wizard-continue', 3000);
  if (!button || ctx.isCancelled() || button.hasAttribute('disabled')) {
    return;
  }
  await clickAnimated(button, ctx);
  await delay(400);
}

/** Type the dummy name, then move on to the contracts step. */
async function nameViewAndContinue(ctx: TourActionContext): Promise<void> {
  const input = await waitForTourElement('views.name-input', 3000);
  if (!input || ctx.isCancelled()) {
    return;
  }
  input.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(input);
  if (ctx.animate) {
    await delay(500);
  }
  await typeInto(input, 'Sales Contracts View', ctx, { charMs: 28 });
  if (ctx.animate) {
    await delay(400);
  }
  await clickWizardContinue(ctx);
}

/**
 * Tick the first folder or contract in the vault picker, unless something is
 * already picked. Ticking goes through the checkbox: the rest of a folder row
 * includes the name, and clicking that opens the folder instead.
 */
async function pickFirstVaultRow(ctx: TourActionContext): Promise<void> {
  const picker = await waitForTourElement('views.vault-picker', 3000);
  if (!picker || ctx.isCancelled() || picker.querySelector('.row.picked')) {
    return;
  }
  const checkbox = await waitForCondition(
    () =>
      picker.querySelector<HTMLElement>('.row.pickable .n-checkbox') ??
      (tableSettledEmpty(picker) ? 'empty' : null),
    3000
  );
  if (!checkbox || checkbox === 'empty' || ctx.isCancelled()) {
    return;
  }
  ctx.setHighlight(checkbox.closest<HTMLElement>('.row') ?? checkbox);
  if (ctx.animate) {
    await delay(500);
  }
  await clickAnimated(checkbox, ctx);
  if (ctx.animate) {
    // Give the preview a moment to start filling from the pick — the next
    // scene points straight at it.
    await delay(700);
  }
}

async function openColumnBuilder(ctx: TourActionContext): Promise<void> {
  const button = await waitForTourElement('views.add-column', 3000);
  if (!button || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(button, ctx);
}

/**
 * Spotlight one data cell of the finished view's grid: the cell gets a
 * runtime-stamped anchor so the NEXT scene can attach its bubble to it.
 * The stamp is transient DOM state — any grid re-render clears it, and a
 * missing stamp just skips the cell scene (empty views stay safe).
 */
async function spotlightSampleCell(ctx: TourActionContext): Promise<void> {
  const grid = await waitForTourElement('views.grid', 3000);
  if (!grid || ctx.isCancelled()) {
    return;
  }
  for (const stale of document.querySelectorAll('[data-tour="views.sample-cell"]')) {
    stale.removeAttribute('data-tour');
  }
  // The sample must be a VALUE cell holding a real answer. Per row, the
  // Name column is the one carrying the expand toggler (a row-number cell
  // may precede it) — candidates are the columns after it, skipping cells
  // that are empty or still extracting (spinner / "Pending"). Rows are
  // searched in order until one offers a settled value; the pending-cell
  // fallback keeps the scene alive when nothing has finished yet.
  const rows = [...grid.querySelectorAll<HTMLElement>('tbody tr')];
  const valueCells = (r: HTMLElement): HTMLElement[] => {
    const cells = [...r.querySelectorAll<HTMLElement>('td')];
    const nameIndex = cells.findIndex(c => c.querySelector('.p-treetable-toggler') !== null);
    return cells.slice(nameIndex === -1 ? 2 : nameIndex + 1);
  };
  const isSettled = (c: HTMLElement): boolean =>
    c.textContent.trim().length > 0 &&
    !/pending/i.test(c.textContent) &&
    c.querySelector('.n-spin, .cell-pending-spinner') === null;
  const cell =
    rows.flatMap(valueCells).find(isSettled) ?? (rows[0] ? valueCells(rows[0])[0] : undefined);
  if (!cell) {
    return;
  }
  cell.dataset.tour = 'views.sample-cell';
  cell.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(cell);
  if (ctx.animate) {
    await delay(600);
  }
}

/** Close a drawer via its own header close button. */
async function closeDrawer(key: string, ctx: TourActionContext): Promise<void> {
  const drawer = document.querySelector<HTMLElement>(`[data-tour="${key}"]`);
  const close = drawer?.querySelector<HTMLElement>('.n-base-close');
  if (!drawer || !close || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(close, ctx);
  await delay(300);
}

async function closeColumnBuilderAndContinue(ctx: TourActionContext): Promise<void> {
  await closeDrawer('views.column-drawer', ctx);
  if (ctx.isCancelled()) {
    return;
  }
  await clickWizardContinue(ctx);
}

/**
 * From the Columns popover, open the first AI column's editor (the drawer
 * where prompt and filters live). The popover closes itself when the editor
 * opens; if the view has no AI columns, just close the popover again.
 */
async function openFirstColumnEditor(ctx: TourActionContext): Promise<void> {
  const wrapper = await waitForTourElement('views.columns', 3000);
  const trigger = wrapper?.querySelector<HTMLElement>('button');
  if (!wrapper || !trigger || ctx.isCancelled()) {
    return;
  }
  await clickAnimated(trigger, ctx);
  const edit = await waitForCondition(() => {
    const el = document.querySelector<HTMLElement>('[data-tour="views.column-edit"]');
    return el && el.getClientRects().length > 0 ? el : null;
  }, 3000);
  if (!edit || ctx.isCancelled()) {
    // No AI columns to edit — toggle the popover back shut.
    await clickAnimated(trigger, ctx);
    return;
  }
  if (ctx.animate) {
    await delay(600);
  }
  await clickAnimated(edit, ctx);
}

const closeColumnEditor = (ctx: TourActionContext): Promise<void> =>
  closeDrawer('views.edit-drawer', ctx);

/**
 * "Is the wizard currently ON this step?" — used as a prerequisite so that
 * backward skips through builder scenes are instant instead of waiting out
 * hidden targets. Only safe on scenes reached from the SAME page (v-show
 * flips synchronously); scenes entered via navigation keep the target wait.
 */
const isStepVisible = (key: string) => (): boolean => {
  const el = document.querySelector<HTMLElement>(`[data-tour="${key}"]`);
  return el !== null && el.getClientRects().length > 0;
};

/** Back into the builder (Back from the "existing view" scene). */
async function reopenBuilder(): Promise<void> {
  try {
    await getRouter().push({ name: 'contract-management-view-create' });
  } catch {
    // Navigation declined — the scene's target wait decides.
  }
  await delay(400);
}

export const viewsTour: TourDefinition = {
  key: 'contract-views',
  title: 'Contract Views',
  description: 'Design tables that GAIA fills from your contract files.',
  entryRoute: 'contract-management-views',
  requiredPermission: 'can_use_contract_management',
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'contract-management-views' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'list',
      route: 'contract-management-views',
      target: 'views.list-table',
      placement: 'top',
      title: 'Your views',
      body:
        'A view is a table you design over your contracts: you pick the ' +
        'columns, and GAIA answers them from each contract’s files. ' +
        'First, let’s see how one is built.',
    },
    {
      id: 'create',
      route: 'contract-management-views',
      target: 'views.new-button',
      placement: 'bottom-end',
      title: 'Building a view',
      body: 'It starts here. I will open the builder for you.',
      actions: [{ kind: 'click', target: 'views.new-button' }],
    },
    {
      id: 'name',
      target: 'views.step-details',
      placement: 'bottom',
      title: 'Step 1: name it',
      body:
        'A clear name says what the table answers. I will fill in a demo ' + 'name and continue.',
      actions: [{ kind: 'custom', run: nameViewAndContinue }],
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'contracts',
      target: 'views.step-contracts',
      placement: 'bottom',
      title: 'Step 2: pick the contracts',
      body:
        'The view pulls its contracts from the folders and contracts you ' +
        'select in the vault. I will pick one.',
      actions: [{ kind: 'custom', run: pickFirstVaultRow }],
      // Rewind the wizard with the tour, or the bubbles and the builder
      // drift out of step.
      backActions: [{ kind: 'click', target: 'views.wizard-back' }],
    },
    {
      id: 'preview',
      // Only meaningful while the wizard is on the contracts step; skips
      // instantly elsewhere (e.g. backing into the builder from later on).
      prerequisite: isStepVisible('views.step-contracts'),
      target: 'views.preview',
      placement: 'left',
      title: 'Watch the preview as you build',
      body:
        'The preview fills with the picked contracts right away. At every ' +
        'step, check here to see how your changes shape the view. ' +
        'Let’s continue.',
      actions: [{ kind: 'custom', run: clickWizardContinue }],
    },
    {
      id: 'columns-step',
      prerequisite: isStepVisible('views.step-columns'),
      target: 'views.step-columns',
      placement: 'bottom',
      title: 'Step 3: define the columns',
      body:
        'Columns are the questions GAIA answers for every contract: end ' +
        'dates, notice periods, parties, anything. Let me open the column ' +
        'builder.',
      actions: [{ kind: 'custom', run: openColumnBuilder }],
      backActions: [{ kind: 'click', target: 'views.wizard-back' }],
    },
    {
      id: 'column-drawer',
      // The drawer only opens from the columns step; if the wizard never
      // reached it (e.g. nothing to pick), skip instantly.
      prerequisite: isStepVisible('views.step-columns'),
      target: 'views.column-drawer',
      placement: 'left',
      title: 'One column, one question',
      body:
        'Give the column a name and a prompt, or import one from your ' +
        'prompt library and collections. I will close it and continue.',
      actions: [{ kind: 'custom', run: closeColumnBuilderAndContinue }],
      // Going Back must not leave the builder drawer hanging open over the
      // previous scene.
      backActions: [{ kind: 'custom', run: ctx => closeDrawer('views.column-drawer', ctx) }],
    },
    {
      id: 'visibility',
      prerequisite: isStepVisible('views.step-visibility'),
      target: 'views.step-visibility',
      placement: 'bottom',
      title: 'Step 4: who sees it',
      body:
        'Keep the view to yourself or share it with the organisation. ' +
        'Create View would save it; we will leave the builder without ' +
        'saving anything.',
      backActions: [{ kind: 'click', target: 'views.wizard-back' }],
    },
    {
      id: 'existing',
      route: 'contract-management-views',
      target: 'views.list-table',
      placement: 'top',
      title: 'Now a finished view',
      body: 'Let’s open an existing view and see what all this produces.',
      actions: [{ kind: 'custom', run: openFirstView }],
      backActions: [{ kind: 'custom', run: reopenBuilder }],
    },
    {
      id: 'grid',
      prerequisite: viewOpen,
      target: 'views.grid',
      placement: 'top',
      title: 'One row per contract',
      body:
        'Each row is a contract the view picked, and every cell is ' +
        'the value GAIA extracted for that row, column by column. Let me ' +
        'point at one.',
      actions: [{ kind: 'custom', run: spotlightSampleCell }],
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'cell',
      prerequisite: viewOpen,
      target: 'views.sample-cell',
      placement: 'bottom',
      title: 'One cell, one answer',
      body:
        'This cell is the column’s answer for this row’s contract. Every ' +
        'cell in the grid works the same way.',
    },
    {
      id: 'columns-popover',
      prerequisite: viewOpen,
      target: 'views.columns',
      placement: 'bottom-end',
      title: 'Manage the columns',
      body:
        'All the view’s columns live here: show or hide them, or open one ' +
        'to edit. I will open the first column’s editor.',
      actions: [{ kind: 'custom', run: openFirstColumnEditor }],
    },
    {
      id: 'column-editor',
      prerequisite: viewOpen,
      target: 'views.edit-drawer',
      placement: 'left',
      title: 'Tune the column',
      body:
        'Rename it, refine its prompt, and add filters here to slice the ' +
        'table by this column’s values. I will close it.',
      actions: [{ kind: 'custom', run: closeColumnEditor }],
      // Going Back must close the editor too, not leave it over the popover
      // scene.
      backActions: [{ kind: 'custom', run: closeColumnEditor }],
    },
    {
      id: 'finale',
      title: 'That is Contract Views',
      body:
        'Design the table once, and every contract you add gets the same ' +
        'questions answered. Build one for renewals, liabilities, or ' +
        'whatever you track.',
    },
  ],
};
